import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, CheckCircle, Loader2, ArrowRight, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';
import { format, startOfToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import Footer from '../components/Footer';
import { Calendar as CalendarComponent } from '../components/ui/calendar';

const BookingPage = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    service_type: 'electricite',
    description: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    address: '',
  });

  useEffect(() => {
    if (selectedDate) {
      setSelectedSlot(null); // Reset le créneau choisi si on change de date
      fetchAvailability(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    if (publicKey) {
      try {
        emailjs.init(publicKey);
      } catch (e) {
        console.warn('EmailJS init failed', e);
      }
    }
  }, []);

  const fetchAvailability = (dateStr) => {
    setSlotsLoading(true);

    // Tous tes créneaux de base
    const allSlots = ["08:00", "09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00"];

    // 1. RÉCUPÉRATION DES VRAIS RDV DEPUIS LE LOCALSTORAGE
    const savedAppointments = JSON.parse(localStorage.getItem('my_booked_slots')) || {};
    // Structure attendue : { "2026-06-15": ["14:00", "16:00"] }
    const realBookedSlotsForThisDay = savedAppointments[dateStr] || [];

    // On filtre pour enlever les créneaux déjà VRAIMENT réservés par l'utilisateur
    let filteredSlots = allSlots.filter(slot => !realBookedSlotsForThisDay.includes(slot));

    // 2. SIMULATION DE FAUX CLIENTS (Uniquement s'il reste des places)
    if (filteredSlots.length > 2) {
      // On prend entre 1 et 3 créneaux au hasard pour faire croire que d'autres ont réservé
      const numberOfFakeReservations = Math.floor(Math.random() * 3) + 1;
      const shuffled = [...filteredSlots].sort(() => 0.5 - Math.random());
      const fakeBlocked = shuffled.slice(0, numberOfFakeReservations);

      filteredSlots = filteredSlots.filter(s => !fakeBlocked.includes(s));
    }

    setAvailableSlots(filteredSlots);
    setSlotsLoading(false);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot) {
      toast.error('Veuillez sélectionner une date et un créneau');
      return;
    }

    setLoading(true);
    const dateStr = format(selectedDate, 'yyyy-MM-dd');

    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_6wrtsqj';
      const templateId = process.env.REACT_APP_EMAILJS_BOOKING_TEMPLATE_ID || process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID || 'template_njb9erq';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      const subject = `Rendez-vous: ${formData.service_type} - ${format(selectedDate, 'dd/MM/yyyy')} ${selectedSlot}`;

      const templateParams = {
        subject,
        from_name: formData.client_name,
        from_email: formData.client_email,
        phone: formData.client_phone,
        message: `Date: ${format(selectedDate, 'EEEE dd MMMM yyyy', { locale: fr })}\nHeure: ${selectedSlot}\nAdresse: ${formData.address}\n\nDescription:\n${formData.description}`,
        service_type: formData.service_type,
        to_email: 'aldroubi.brico@gmail.com',
      };

      if (serviceId && templateId && publicKey) {
        await emailjs.send(serviceId, templateId, templateParams, publicKey);
      }

      // 3. SAUVEGARDE DU VRAI RDV DANS LE LOCALSTORAGE
      const savedAppointments = JSON.parse(localStorage.getItem('my_booked_slots')) || {};
      if (!savedAppointments[dateStr]) {
        savedAppointments[dateStr] = [];
      }
      // On ajoute le créneau choisi à la liste des créneaux bloqués pour cette date
      savedAppointments[dateStr].push(selectedSlot);
      localStorage.setItem('my_booked_slots', JSON.stringify(savedAppointments));

      setSuccess(true);
      toast.success('Rendez-vous confirmé !');
    } catch (error) {
      console.error('Booking error:', error);
      // Fallback mailto
      try { await navigator.clipboard.writeText('aldroubi.brico@gmail.com'); toast.error('Adresse copiée. Ouverture du client mail...'); } catch (cErr) {}
      const mailtoBody = `Date: ${format(selectedDate, 'EEEE dd MMMM yyyy', { locale: fr })}\nHeure: ${selectedSlot}\n\nNom: ${formData.client_name}\nTéléphone: ${formData.client_phone}\nEmail: ${formData.client_email}\nAdresse: ${formData.address}`;
      window.location.href = `mailto:aldroubi.brico@gmail.com?subject=RDV&body=${encodeURIComponent(mailtoBody)}`;
    } finally {
      setLoading(false);
    }
  };

  const today = startOfToday();
  const disabledDays = [{ before: today }];

  if (success) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 pb-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="luxury-card p-8 sm:p-12">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-green- green-500" />
            </div>
            <h1 className="font-serif text-2xl text-white mb-4">Rendez-vous Confirmé !</h1>
            <p className="text-gray-400 mb-8">
              Enregistré pour le <span className="text-gold font-medium">{selectedDate && format(selectedDate, 'EEEE dd MMMM yyyy', { locale: fr })}</span> à <span className="text-gold font-medium">{selectedSlot}</span>.
            </p>
            <button
              onClick={() => {
                setSuccess(false);
                setSelectedDate(null);
                setSelectedSlot(null);
                setFormData({ service_type: 'electricite', description: '', client_name: '', client_email: '', client_phone: '', address: '' });
              }}
              className="btn-gold"
            >
              Nouveau Rendez-vous
            </button>
          </motion.div>
        </div>
        <div className="mt-24"><Footer /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-8">
          <span className="luxury-label">Rendez-vous</span>
          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl text-white mt-4">
            Réservez Votre <span className="text-gold">Intervention</span>
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Calendrier */}
          <div className="luxury-card p-4 sm:p-6">
            <h2 className="font-serif text-lg text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gold" /> Sélectionnez une date
            </h2>
            <div className="flex justify-center">
              <CalendarComponent mode="single" selected={selectedDate} onSelect={setSelectedDate} disabled={disabledDays} locale={fr} className="rounded-md border border-white/10" />
            </div>

            {selectedDate && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gold" /> Créneaux disponibles
                </h3>

                {slotsLoading ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 text-gold animate-spin" /></div>
                ) : availableSlots && availableSlots.length > 0 ? (
                  <>
                    <div className="grid grid-cols-4 gap-2">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot}
                          onClick={() => setSelectedSlot(slot)}
                          className={`py-2.5 rounded text-xs sm:text-sm font-medium transition-all ${selectedSlot === slot ? 'bg-gold text-black' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                    {availableSlots.length < 8 && (
                      <p className="text-orange-400 text-xs mt-3 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Certains créneaux sont déjà réservés par d'autres clients
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-gray-500 text-center py-4 text-sm">Aucun créneau disponible pour cette date</p>
                )}
              </motion.div>
            )}
          </div>

          {/* Formulaire */}
          <div className="luxury-card p-4 sm:p-6">
            <h2 className="font-serif text-lg text-white mb-4">Vos Informations</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="luxury-label">Type de service</label>
                <select name="service_type" value={formData.service_type} onChange={handleInputChange} className="luxury-input">
                  <option value="electricite">Électricité</option>
                  <option value="peinture">Peinture</option>
                  <option value="revetement">Revêtements</option>
                </select>
              </div>

              <div>
                <label className="luxury-label">Description du besoin</label>
                <textarea name="description" value={formData.description} onChange={handleInputChange} rows={3} className="luxury-input resize-none" placeholder="Détails..." required />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="luxury-label">Nom</label>
                  <input type="text" name="client_name" value={formData.client_name} onChange={handleInputChange} className="luxury-input" required />
                </div>
                <div>
                  <label className="luxury-label">Téléphone</label>
                  <input type="tel" name="client_phone" value={formData.client_phone} onChange={handleInputChange} className="luxury-input" required />
                </div>
              </div>

              <div>
                <label className="luxury-label">Email</label>
                <input type="email" name="client_email" value={formData.client_email} onChange={handleInputChange} className="luxury-input" required />
              </div>

              <div>
                <label className="luxury-label">Adresse</label>
                <input type="text" name="address" value={formData.address} onChange={handleInputChange} className="luxury-input" required />
              </div>

              {selectedDate && selectedSlot && (
                <div className="bg-gold/10 border border-gold/20 rounded p-4">
                  <p className="text-white text-sm">{format(selectedDate, 'EEEE dd MMMM yyyy', { locale: fr })} à {selectedSlot}</p>
                </div>
              )}

              <button type="submit" disabled={loading || !selectedDate || !selectedSlot} className="w-full btn-gold flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Confirmer le Rendez-vous <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          </div>
        </div>
      </div>
      <div className="mt-24"><Footer /></div>
    </div>
  );
};

export default BookingPage;